import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { SelectItem } from 'primeng/api';
import { ConfirmationService } from 'primeng/api';
import { PlantWithSectors } from '../../../models/plant-with-sectors.model';
import { ClearUserSelectedDataRequest } from '../../../models/requests/clear-user-selected-data-request';

@Component({
  selector: 'app-data-cleaning-panel',
  templateUrl: './data-cleaning-panel.component.html',
  styleUrl: './data-cleaning-panel.component.scss',
  standalone: false,
})
export class DataCleaningPanelComponent implements OnChanges {
  @Input() title = '';
  @Input() description = '';
  @Input() plantsWithSectors: PlantWithSectors[] = [];
  @Input() isLoading = false;
  @Input() confirmKey = 'data-cleaning-confirm';

  @Output() clearRequested = new EventEmitter<ClearUserSelectedDataRequest>();

  plantOptions: SelectItem[] = [{ label: 'Toutes les usines', value: null }];
  mainWorkCenterOptions: SelectItem[] = [];

  selectedPlant: string | null = null;
  selectedWorkCenters: string[] = [];

  constructor(private confirmationService: ConfirmationService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['plantsWithSectors']) {
      this.buildPlantOptions();
      this.updateMainWorkCenterOptions();
    }
  }

  onPlantChange(): void {
    this.selectedWorkCenters = [];
    this.updateMainWorkCenterOptions();
  }

  private buildPlantOptions(): void {
    this.plantOptions = [
      { label: 'Toutes les usines', value: null },
      ...this.plantsWithSectors.map((p) => ({
        label: p.Plant,
        value: p.Plant,
      })),
    ];
  }

  private updateMainWorkCenterOptions(): void {
    if (!this.selectedPlant) {
      const allMwcs = [
        ...new Set(this.plantsWithSectors.flatMap((p) => p.MainWorkCenters)),
      ].sort();
      this.mainWorkCenterOptions = allMwcs.map((s) => ({ label: s, value: s }));
    } else {
      const found = this.plantsWithSectors.find(
        (p) => p.Plant === this.selectedPlant,
      );
      this.mainWorkCenterOptions = found
        ? found.MainWorkCenters.map((s) => ({ label: s, value: s }))
        : [];
    }
  }

  private get hasNoFilters(): boolean {
    return !this.selectedPlant && this.selectedWorkCenters.length === 0;
  }

  onClearClick(): void {
    const message = this.hasNoFilters
      ? '⚠️ Aucun filtre sélectionné. Cette opération effacera les données pour <strong>toutes les usines et tous les postes de travail</strong>. Voulez-vous continuer?'
      : `Voulez-vous effacer les données pour l'usine "${this.selectedPlant ?? 'toutes'}"` +
        (this.selectedWorkCenters.length > 0
          ? ` et les postes de travail sélectionnés`
          : '') +
        ' ?';

    const header = this.hasNoFilters
      ? '⚠️ Avertissement — Opération globale'
      : 'Confirmer le nettoyage';

    this.confirmationService.confirm({
      key: this.confirmKey,
      message,
      header,
      icon: this.hasNoFilters
        ? 'pi pi-exclamation-triangle'
        : 'pi pi-info-circle',
      acceptLabel: 'Oui, effacer',
      rejectLabel: 'Annuler',
      acceptButtonStyleClass: this.hasNoFilters
        ? 'p-button-danger'
        : 'p-button-primary',
      accept: () => {
        this.clearRequested.emit({
          SapPlant: this.selectedPlant
            ? parseInt(this.selectedPlant, 10)
            : null,
          SapMainWorkCenters: [...this.selectedWorkCenters],
        });
      },
    });
  }
}
